import { LocaleLink } from "../i18n/LocaleLink";
import { Container } from "./ui";
import { IndustriesMarquee } from "./IndustriesMarquee";
import { useLocale } from "../i18n/useLocale";
import { useSwitchLocale } from "../i18n/useSwitchLocale";
import { LIVE_LOCALES } from "../i18n/locales";
import { featureLiveForMarket } from "../content/flags";
import { TRIAL_PATH } from "../config/entrypoints";

// Footer columns. `k` is the i18n key suffix; the visible heading
// (`footer.col.<k>`) and each link label (`footer.link.<lk>`) resolve via `t()`.
const COLS = [
  {
    k: "product",
    links: [
      { lk: "features", to: "/features" },
      { lk: "projectManagement", to: "/project-management" },
      { lk: "pricing", to: "/pricing" },
      { lk: "industries", to: "/industries" },
      { lk: "freeTrial", to: TRIAL_PATH },
    ],
  },
  {
    k: "belgium",
    links: [
      { lk: "law2027", to: "/law-2027" },
      { lk: "resources", to: "/resources" },
      { lk: "faq", to: "/faq" },
    ],
  },
  {
    k: "company",
    links: [
      { lk: "about", to: "/about" },
      { lk: "contact", to: "/contact" },
    ],
  },
];

export default function Footer() {
  const { t, market, locale } = useLocale();
  const switchLocale = useSwitchLocale();
  // Language chips + the market-named column follow the ACTIVE market
  // (registry-driven): only that market's LIVE locales, so en-be shows up under
  // Belgium the moment it flips live, and International never shows a "Belgium" head.
  const langs = LIVE_LOCALES.filter((l) => l.market === market);
  // The `/law-2027` link is belgium-only and further gated by the `law_2027`
  // kill-switch; drop it elsewhere (and when an admin disables it).
  const lawShown = market === "belgium" && featureLiveForMarket(market, "law_2027");
  return (
    <footer className="bg-ds-surface-dark text-white">
      {/* Industry strips — full-bleed, above the footer info, two rows opposite ways.
          Extra top padding so the visible gap above the strip clears the previous
          section's rounded-bottom overlap (IndustryCta -mb-10) to ~32px. */}
      <div className="flex flex-col gap-6 pt-[60px] pb-12">
        <IndustriesMarquee className="py-1" durationSec={60} />
        <IndustriesMarquee className="py-1" durationSec={60} reverse />
      </div>
      <Container className="pt-[72px] pb-14">
        <div className="flex flex-col justify-between gap-10 md:flex-row">
          <div className="max-w-xs">
            <LocaleLink to="/" className="inline-flex items-center" aria-label={t("common:nav.brandHomeAria")}>
              {/* Official Done-it logo — reverse (white-wordmark) variant for the dark
                  footer, derived from the supplied brand lockup used in the nav. Replaces
                  the earlier generic clock-mark placeholder — DIT-394. */}
              <img
                src="/done-it-logo-reverse.png"
                alt="Done-it"
                width={116}
                height={40}
                className="h-9 w-auto"
                loading="lazy"
                decoding="async"
              />
            </LocaleLink>
            <p className="mt-4 text-[14px] leading-relaxed text-white/55">
              {t("common:footer.tagline")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
            {COLS.map((col) => (
              <div key={col.k}>
                <div className="font-display text-[13px] font-semibold uppercase tracking-wider text-white/40">
                  {/* The middle column is the active market's — label it with the
                      market name (Belgium / Worldwide), never a hardcoded "Belgium". */}
                  {t(col.k === "belgium" ? `common:nav.market.${market}` : `common:footer.col.${col.k}`)}
                </div>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {col.links
                    .filter((l) => l.to !== "/law-2027" || lawShown)
                    .map((l) => (
                    <li key={l.to}>
                      {/^https?:/.test(l.to) ? (
                        <a href={l.to} target="_blank" rel="noopener noreferrer" className="text-[14px] text-white/70 transition-colors hover:text-white">
                          {t(`common:footer.link.${l.lk}`)}
                        </a>
                      ) : (
                        <LocaleLink to={l.to} className="text-[14px] text-white/70 transition-colors hover:text-white">
                          {t(`common:footer.link.${l.lk}`)}
                        </LocaleLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 text-[13px] text-white/45 sm:flex-row sm:items-center">
          <span>{t("common:footer.copyright", { year: 2026 })}</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {/* Live locales of the active market, not a hardcoded NL/FR/EN — each
                switches the page to that language (BE: NL/FR/EN once en-be is live). */}
            {langs.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => switchLocale(l.code)}
                className={`uppercase transition-colors hover:text-white ${l.code === locale ? "text-white/70" : ""}`}
              >
                {l.code.split("-")[0]}
              </button>
            ))}
            <LocaleLink to="/contact" className="hover:text-white">{t("common:footer.privacyTerms")}</LocaleLink>
          </div>
        </div>
      </Container>
    </footer>
  );
}
